import React from "react"
import { Area, AreaChart, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { ApolloError } from "@apollo/client"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import timezone from "dayjs/plugin/timezone"
import {
  ChartContainer,
  EmptyState,
  Header,
  Title,
  Wrapper
} from "./OperationsOverTimeChart.styled"
import { CustomOperation } from "lib/types/generated/graphql-types"
import {
  formatOperationToInput,
  OPERATION_KEYS,
  operationFillColors,
  operationStrokeColors
} from "../helpers"
import CustomTooltip from "./CustomTooltip"
import { TimeSeriesData } from "./useOperationsOverTimeData"

dayjs.extend(utc)
dayjs.extend(timezone)

interface OrdersOverTimeChartProps {
  chartData: TimeSeriesData[]
  loading: boolean
  error?: ApolloError
  operations: CustomOperation[]
  refetchLoading: boolean
}

export const OperationsOverTimeChart: React.FC<OrdersOverTimeChartProps> = (props) => {
  const { chartData, loading, error, operations, refetchLoading } = props

  const selectedOperationKeys =
    operations.length > 0
      ? (operations.map((op) => formatOperationToInput(op)).filter(Boolean) as string[])
      : OPERATION_KEYS


  return (
    <Wrapper className={(loading || refetchLoading) ? "blur" : ""}>
      <Header>
        <Title>Completed operations</Title>
      </Header>

      {loading ? (
        <EmptyState>Loading...</EmptyState>
      ) : error ? (
        <EmptyState>Error loading chart data</EmptyState>
      ) : chartData.length === 0 ? (
        <EmptyState>No data available</EmptyState>
      ) : (
        <ChartContainer>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={chartData}
              margin={{
                top: 10,
                right: 30,
                left: 0,
                bottom: 0
              }}
            >
              <XAxis
                dataKey="displayLabel"
                tick={{ fontSize: 10 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              {selectedOperationKeys.map((operation) => (
                <Area
                  key={operation}
                  type="monotone"
                  dataKey={operation}
                  stroke={operationStrokeColors[operation]}
                  strokeWidth={1.3}
                  fill={operationFillColors[operation]}
                  name={operation}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </ChartContainer>
      )}
    </Wrapper>
  )
}
